/**
 * HeightTracker - Tracks tower height and win condition
 * Monitors when the tower crosses the Zenith line and maintains stability
 */

import Phaser from 'phaser';
import { GAMEPLAY } from '../config/gameConfig';

/**
 * Tracks tower height and win condition
 */
export class HeightTracker {
  private scene: Phaser.Scene;
  private zenithY: number;
  private stabilityDuration: number;
  private stabilityTimer: Phaser.Time.TimerEvent | null = null;
  private isAboveZenith: boolean = false;
  private onWinCallback: (() => void) | null = null;

  /**
   * @param scene - The Phaser scene
   * @param zenithY - Y position of the Zenith line
   * @param stabilityDuration - Time required above Zenith for win (ms)
   */
  constructor(
    scene: Phaser.Scene,
    zenithY: number = GAMEPLAY.ZENITH_LINE_Y,
    stabilityDuration: number = GAMEPLAY.STABILITY_TIME
  ) {
    this.scene = scene;
    this.zenithY = zenithY;
    this.stabilityDuration = stabilityDuration;
  }

  /**
   * Get the highest point (lowest Y value) of all bodies
   */
  getHighestPoint(bodies: MatterJS.BodyType[]): number {
    if (bodies.length === 0) return Infinity;

    let highest = Infinity;

    for (const body of bodies) {
      const bounds = body.bounds;
      if (bounds) {
        const top = bounds.min.y;
        if (top < highest) {
          highest = top;
        }
      }
    }

    return highest;
  }

  /**
   * Check if win condition is met
   * @param bodies - All placed physics bodies
   * @param onWin - Callback when win condition is achieved
   */
  checkWinCondition(bodies: MatterJS.BodyType[], onWin: () => void): void {
    const highestPoint = this.getHighestPoint(bodies);
    const nowAboveZenith = highestPoint < this.zenithY;

    if (nowAboveZenith && !this.isAboveZenith) {
      // Just crossed above Zenith line
      this.isAboveZenith = true;
      this.onWinCallback = onWin;
      this.startStabilityTimer();
    } else if (!nowAboveZenith && this.isAboveZenith) {
      // Dropped below Zenith line
      this.isAboveZenith = false;
      this.cancelStabilityTimer();
    }
  }

  /**
   * Start the stability timer
   */
  private startStabilityTimer(): void {
    this.cancelStabilityTimer();

    this.stabilityTimer = this.scene.time.delayedCall(
      this.stabilityDuration,
      () => {
        if (this.onWinCallback) {
          this.onWinCallback();
        }
      }
    );
  }

  /**
   * Cancel the stability timer
   */
  private cancelStabilityTimer(): void {
    if (this.stabilityTimer) {
      this.stabilityTimer.destroy();
      this.stabilityTimer = null;
    }
  }

  /**
   * Get time remaining on stability timer (ms)
   */
  getTimeRemaining(): number {
    if (!this.stabilityTimer) return 0;
    return this.stabilityTimer.getRemaining();
  }

  /**
   * Check if stability timer is active
   */
  isTimerActive(): boolean {
    return this.stabilityTimer !== null && !this.stabilityTimer.hasDispatched;
  }

  /**
   * Check if tower is currently above Zenith
   */
  getIsAboveZenith(): boolean {
    return this.isAboveZenith;
  }

  /**
   * Get the Zenith Y position
   */
  getZenithY(): number {
    return this.zenithY;
  }

  /**
   * Reset the tracker state
   */
  reset(): void {
    this.isAboveZenith = false;
    this.cancelStabilityTimer();
    this.onWinCallback = null;
  }

  /**
   * Destroy the tracker
   */
  destroy(): void {
    this.cancelStabilityTimer();
  }
}
